import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PackageCheck, Truck, Store, CheckCircle2, FileText, ToggleLeft, ToggleRight, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function ProviderPharmacy() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [pharmacyOffers, setPharmacyOffers] = useState([]);
  const [deliveryOffers, setDeliveryOffers] = useState([]);
  const [pharmacyOrders, setPharmacyOrders] = useState([]);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [tab, setTab] = useState('requests');
  const [deliveryHistory, setDeliveryHistory] = useState([]);
  const [pickupOtp, setPickupOtp] = useState({});
  const [customerOtp, setCustomerOtp] = useState({});
  const [quoteForm, setQuoteForm] = useState(null);

  const load = async () => {
    try {
      const profile = await api.get('/providers/me');
      const provider = profile.data.provider;
      setMe(provider);
      setDeliveryEnabled(Boolean(provider?.delivery_enabled));

      if (provider?.category === 'pharmacy') {
        const [offers, mine] = await Promise.all([
          api.get('/providers/me/pharmacy/offers'),
          api.get('/providers/me/pharmacy/orders'),
        ]);
        setPharmacyOffers(offers.data.offers || []);
        setPharmacyOrders(mine.data.orders || []);
      } else {
        setPharmacyOffers([]);
        setPharmacyOrders([]);
      }

      if (provider?.delivery_enabled) {
        const [offers, active, completed] = await Promise.all([
          api.get('/providers/me/pharmacy/delivery-offers'),
          api.get('/providers/me/pharmacy/deliveries'),
          api.get('/providers/me/pharmacy/deliveries', { params: { status: 'completed' } }),
        ]);
        setDeliveryOffers(offers.data.offers || []);
        setDeliveryOrders(active.data.orders || []);
        setDeliveryHistory(completed.data.orders || []);
      } else {
        setDeliveryOffers([]);
        setDeliveryOrders([]);
        setDeliveryHistory([]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load Pharmacy operations');
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, []);

  const setDeliveryMode = async () => {
    try {
      const { data } = await api.patch('/providers/me/delivery-mode', { enabled: !deliveryEnabled });
      setDeliveryEnabled(Boolean(data.delivery_enabled));
      toast.success(data.delivery_enabled ? 'Ranger delivery mode enabled' : 'Ranger delivery mode disabled');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change delivery mode');
    }
  };

  const acceptPharmacy = async (offerId) => {
    try {
      await api.post('/providers/me/pharmacy/offers/' + offerId + '/accept');
      toast.success('Pharmacy order accepted');
      setTab('orders');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Offer is no longer available');
    }
  };

  const declinePharmacy = async (offerId) => {
    try {
      await api.post('/providers/me/pharmacy/offers/' + offerId + '/decline');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not decline');
    }
  };
  const openQuote = (offer) => setQuoteForm({
    orderId: offer.order_id,
    subtotal: '',
    medicines: '',
    notes: '',
  });

  const submitQuote = async () => {
    if (!quoteForm) return;
    const subtotal = Number(quoteForm.subtotal);
    const available_medicines = quoteForm.medicines.split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
    if (!Number.isFinite(subtotal) || subtotal <= 0) return toast.error('Enter a valid medicine subtotal');
    if (!available_medicines.length) return toast.error('List the medicines your pharmacy can fulfil');
    try {
      await api.post('/providers/me/pharmacy/orders/' + quoteForm.orderId + '/quote', {
        medicine_subtotal: subtotal,
        available_medicines,
        notes: quoteForm.notes || undefined,
      });
      toast.success('Quotation sent. RESQLY will compare all pharmacy quotes.');
      setQuoteForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create quote');
    }
  };


  const availability = async (orderId, value) => {
    try {
      await api.post('/providers/me/pharmacy/orders/' + orderId + '/availability', { available: value });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update availability');
    }
  };

  const preparing = async (orderId) => {
    try {
      await api.post('/providers/me/pharmacy/orders/' + orderId + '/preparing');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start preparation');
    }
  };

  const ready = async (orderId) => {
    try {
      await api.post('/providers/me/pharmacy/orders/' + orderId + '/ready');
      toast.success('Ranger search started');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not mark ready');
    }
  };

  const acceptDelivery = async (offerId) => {
    try {
      await api.post('/providers/me/pharmacy/delivery-offers/' + offerId + '/accept');
      toast.success('Delivery accepted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delivery already assigned');
    }
  };

  const step = async (orderId, name, otp) => {
    try {
      await api.post('/providers/me/pharmacy/deliveries/' + orderId + '/step/' + name, { otp: otp || undefined });
      toast.success(name === 'verify_pickup' ? 'Pharmacy OTP verified — trip started' : 'Delivery status updated');
      if (name === 'verify_pickup') setPickupOtp((v) => ({ ...v, [orderId]: '' }));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update delivery');
    }
  };

  const isPharmacy = me?.category === 'pharmacy';

  return (
    <MobileShell title="Pharmacy Operations">
      <div className="px-5 py-5 space-y-4" data-testid="provider-pharmacy">
        <button onClick={() => navigate('/provider/dashboard')} className="text-sm text-blue-700 font-semibold inline-flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Dashboard
        </button>

        <div className="resqly-card p-4">
          <div className="flex items-center justify-between">
            <div><div className="font-bold text-slate-900">Ranger delivery mode</div><div className="text-xs text-slate-500">Use this account for Pharmacy last-mile deliveries.</div></div>
            <button onClick={setDeliveryMode}>{deliveryEnabled ? <ToggleRight className="w-10 h-10 text-emerald-600" /> : <ToggleLeft className="w-10 h-10 text-slate-400" />}</button>
          </div>
        </div>

        {isPharmacy && (
          <div className="flex gap-2">
            <button className={'flex-1 rounded-xl px-3 py-2 text-sm font-semibold ' + (tab === 'requests' ? 'bg-blue-700 text-white' : 'bg-slate-100')} onClick={() => setTab('requests')}>Requests</button>
            <button className={'flex-1 rounded-xl px-3 py-2 text-sm font-semibold ' + (tab === 'orders' ? 'bg-blue-700 text-white' : 'bg-slate-100')} onClick={() => setTab('orders')}>My pharmacy orders</button>
          </div>
        )}

        {isPharmacy && tab === 'requests' && pharmacyOffers.map((offer) => (
          <div key={offer.id} className="resqly-card p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Store className="text-emerald-700" />
              <div>
                <div className="font-semibold">{offer.order_type === 'prescription' ? 'New prescription request' : 'New pharmacy request'}</div>
                <div className="text-xs text-slate-500">Order {offer.order_id.slice(0, 8)} · {offer.distance_km} km</div>
                {offer.order_type === 'prescription' && <div className="text-xs text-blue-700 mt-1">Prescription review · customer pays after quote</div>}
              </div>
            </div>
            <div className="flex gap-2">
              {offer.order_type === 'prescription'
                ? <Button className="flex-1" onClick={() => openQuote(offer)}>Review & quote</Button>
                : <Button className="flex-1" onClick={() => acceptPharmacy(offer.id)}>Accept</Button>}
              <Button variant="outline" className="flex-1" onClick={() => declinePharmacy(offer.id)}>Decline</Button>
            </div>
          </div>
        ))}

        {isPharmacy && tab === 'orders' && pharmacyOrders.map((order) => (
          <div key={order.id} className="resqly-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="font-semibold">#{order.id.slice(0, 8)}</div>
              <span className="text-xs font-bold text-blue-700">{order.status}</span>
            </div>

            {order.order_type === 'prescription' ? (
              <div className="rounded-xl bg-blue-50 border border-blue-100 p-3 space-y-2">
                <div className="flex items-center gap-2 font-semibold text-blue-900"><FileText className="w-4 h-4" /> Prescription request</div>
                <div className="text-xs text-slate-600">Review the customer prescription before quoting. The customer has not paid yet.</div>
                {order.prescription_request_notes && <div className="text-xs text-slate-700">Customer note: {order.prescription_request_notes}</div>}
                {order.prescription?.image_url && (
                  <a href={order.prescription.image_url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-blue-700 underline">Open prescription</a>
                )}
                {order.status === 'pharmacy_accepted' && order.quote_status !== 'ready' && (
                  <Button className="w-full" onClick={() => openQuote({ order_id: order.id })}>Review & quote</Button>
                )}
                {order.status === 'pharmacy_accepted' && order.quote_status === 'ready' && (
                  <div className="text-sm text-emerald-700 font-semibold">Quote sent · waiting for customer payment</div>
                )}
              </div>
            ) : (
              <div className="space-y-1 text-sm">
                {(order.items || []).map((item) => <div key={item.medicine_id}>{item.name} × {item.quantity}</div>)}
              </div>
            )}

            {order.order_type !== 'prescription' && order.status === 'pharmacy_accepted' && (
              <div className="flex gap-2">
                <Button onClick={() => availability(order.id, true)}>Items available</Button>
                <Button variant="outline" onClick={() => availability(order.id, false)}>Unavailable</Button>
              </div>
            )}
            {order.status === 'pharmacy_confirmed' && <Button className="w-full" onClick={() => preparing(order.id)}><PackageCheck className="w-4 h-4 mr-2" /> Start preparing</Button>}
            {order.status === 'preparing' && <Button className="w-full" onClick={() => ready(order.id)}>Ready for Ranger</Button>}
          </div>
        ))}

        {deliveryEnabled && (
          <>
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-2"><Truck className="w-4 h-4" /> Delivery offers</div>
            {deliveryOffers.map((offer) => (
              <div key={offer.id} className="resqly-card p-4 flex items-center gap-3">
                <div className="flex-1"><div className="font-semibold">Pharmacy delivery</div><div className="text-xs text-slate-500">Order {offer.order_id.slice(0, 8)} · {offer.distance_km} km to pickup</div></div>
                <Button onClick={() => acceptDelivery(offer.id)}>Accept</Button>
              </div>
            ))}
          </>
        )}

        {deliveryEnabled && (
          <div className="space-y-3">
            {deliveryHistory.length > 0 && (
              <div className="resqly-card p-4 space-y-2">
                <div className="font-semibold text-slate-900">Delivery history</div>
                {deliveryHistory.slice(0, 10).map((item) => (
                  <div key={item.id} className="flex justify-between gap-3 text-sm border-b last:border-0 pb-2 last:pb-0">
                    <span>#{item.id.slice(0,8)} · {item.completed_at ? new Date(item.completed_at).toLocaleString() : 'Completed'}</span>
                    <span className="font-semibold text-emerald-700">₹{Number(item.payout_amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
            {deliveryOrders.filter((o) => ['ranger_assigned','ranger_arriving_pickup','ready_for_handover','picked_up','ranger_arriving_customer','out_for_delivery'].includes(o.status)).map((order) => (
              <div key={'delivery-' + order.id} className="resqly-card p-4 space-y-3">
                <div className="flex justify-between"><span className="font-semibold">Delivery #{order.id.slice(0,8)}</span><span className="text-xs text-blue-700 font-bold">{order.status}</span></div>
                {order.status === 'ranger_assigned' && <Button className="w-full" onClick={() => step(order.id, 'arrive_pickup')}>Arrived at pharmacy</Button>}
                {order.status === 'ranger_arriving_pickup' && (
                  <div className="space-y-2">
                    <div className="text-xs text-slate-500">Ask the pharmacy for the handover OTP. Verifying it starts your trip.</div>
                    <div className="flex gap-2">
                      <Input value={pickupOtp[order.id] || ''} onChange={(e) => setPickupOtp((v) => ({ ...v, [order.id]: e.target.value.replace(/\D/g, '').slice(0, 6) }))} inputMode="numeric" placeholder="Pharmacy OTP" />
                      <Button disabled={(pickupOtp[order.id] || '').length < 4} onClick={() => step(order.id, 'verify_pickup', pickupOtp[order.id])}>Start trip</Button>
                    </div>
                  </div>
                )}
                {order.status === 'picked_up' && <Button className="w-full" onClick={() => step(order.id, 'arrive_customer')}>Arrived at customer</Button>}
                {order.status === 'ranger_arriving_customer' && <Button className="w-full" onClick={() => step(order.id, 'out_for_delivery')}>Start delivery</Button>}
                {order.status === 'out_for_delivery' && (
                  <div className="space-y-2">
                    <div className="text-xs text-slate-500">Ask the customer for the delivery OTP only at handover.</div>
                    <div className="flex gap-2">
                      <Input value={customerOtp[order.id] || ''} onChange={(e) => setCustomerOtp((v) => ({ ...v, [order.id]: e.target.value.replace(/\D/g, '').slice(0, 6) }))} inputMode="numeric" placeholder="Customer OTP" />
                      <Button disabled={(customerOtp[order.id] || '').length < 4} className="bg-emerald-700" onClick={() => step(order.id, 'complete', customerOtp[order.id])}><CheckCircle2 className="w-4 h-4 mr-2" /> Deliver</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {!isPharmacy && !deliveryEnabled && (
          <div className="resqly-card p-5 text-center text-sm text-slate-600">Enable Ranger delivery mode to receive Pharmacy delivery offers.</div>
        )}

        {quoteForm && (
          <div className="fixed inset-0 z-50 bg-slate-950/50 flex items-end sm:items-center justify-center p-4" onClick={() => setQuoteForm(null)}>
            <div className="w-full max-w-lg rounded-3xl bg-white p-5 shadow-2xl space-y-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Pharmacy quotation</div>
                  <div className="text-xl font-black mt-1">Tell us what you can fulfil</div>
                  <div className="text-xs text-slate-500 mt-1">Distance and ETA are calculated by RESQLY from your pharmacy location.</div>
                </div>
                <button onClick={() => setQuoteForm(null)}><X /></button>
              </div>
              <Input type="number" min="1" value={quoteForm.subtotal} onChange={(e) => setQuoteForm({...quoteForm, subtotal: e.target.value})} placeholder="Medicine subtotal ₹" />
              <textarea className="w-full min-h-28 rounded-2xl border p-3 text-sm" value={quoteForm.medicines} onChange={(e) => setQuoteForm({...quoteForm, medicines: e.target.value})} placeholder="Medicines you can fulfil — one per line or comma separated" />
              <textarea className="w-full min-h-20 rounded-2xl border p-3 text-sm" value={quoteForm.notes} onChange={(e) => setQuoteForm({...quoteForm, notes: e.target.value})} placeholder="Optional note for the customer" />
              <Button className="w-full bg-emerald-700" onClick={submitQuote}>Send quotation</Button>
            </div>
          </div>
        )}
      </div>
    </MobileShell>
  );
}
